import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useReveal } from "@/hooks/useReveal";

const teamMembers = [
  {
    name: "Alex Vantory",
    role: "Founder & CEO",
    bio: "15+ years in software engineering and digital product strategy. Previously led engineering teams at two successful tech startups.",
    specialties: ["Strategy", "Architecture", "Leadership"],
    icon: "ri-user-star-line",
  },
  {
    name: "Diana Kovaleva",
    role: "Head of Development",
    bio: "Full-stack expert with deep knowledge of modern frameworks, cloud infrastructure, and scalable system design.",
    specialties: ["Full-stack", "Cloud", "DevOps"],
    icon: "ri-code-s-slash-line",
  },
  {
    name: "Marcus Chen",
    role: "Lead UI/UX Designer",
    bio: "Award-winning designer focused on creating intuitive digital experiences that users love and businesses benefit from.",
    specialties: ["UI/UX", "Design Systems", "Branding"],
    icon: "ri-palette-line",
  },
  {
    name: "Sarah Whitfield",
    role: "Head of Marketing",
    bio: "Digital marketing strategist with proven track record in growth hacking, SEO, and building brands that stand out.",
    specialties: ["SEO", "Growth", "Content"],
    icon: "ri-megaphone-line",
  },
  {
    name: "James Okafor",
    role: "Senior QA Engineer",
    bio: "Quality assurance expert ensuring every product we deliver meets the highest standards of reliability and performance.",
    specialties: ["Automation", "Testing", "CI/CD"],
    icon: "ri-shield-check-line",
  },
  {
    name: "Elena Petrova",
    role: "Project Manager",
    bio: "Certified PMP with expertise in agile methodologies, stakeholder management, and delivering complex projects on time.",
    specialties: ["Agile", "Scrum", "Delivery"],
    icon: "ri-calendar-check-line",
  },
];

const timeline = [
  { year: "2018", title: "The beginning", body: "Vantory Labs started as a small development team of three engineers working out of a shared office in London. First clients were local startups needing MVPs." },
  { year: "2020", title: "Growing team & expertise", body: "Expanded to 15 people. Added UI/UX design and digital marketing divisions. Delivered our first enterprise-scale project for a fintech client." },
  { year: "2022", title: "International reach", body: "Opened remote-first operations across Europe and North America. 50+ projects delivered. Client retention rate reached 92%." },
  { year: "2024", title: "Full-cycle digital agency", body: "Launched comprehensive digital solutions offering — from custom software and IT consulting to complete marketing strategies. 100+ team members." },
  { year: "2025", title: "Vantory Labs today", body: "A full-service IT company with 50+ specialists, 150+ delivered projects, and clients in 12 countries. Still driven by the same passion for quality." },
];

const values = [
  { icon: "ri-speed-line", title: "Speed", body: "We move fast without cutting corners. Agile processes and clear communication keep every project on track." },
  { icon: "ri-eye-line", title: "Transparency", body: "You always know how your project is progressing. Regular updates, clear reporting, and open communication." },
  { icon: "ri-bar-chart-box-line", title: "Effectiveness", body: "We measure success by your business results. Every feature, every campaign, every line of code serves a purpose." },
  { icon: "ri-team-line", title: "Teamwork", body: "Project managers, developers, designers, marketers, and QA engineers work as one unit dedicated to your success." },
];

export default function StudioPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const storyRef = useReveal<HTMLDivElement>();
  const teamRef = useReveal<HTMLDivElement>();
  const valuesRef = useReveal<HTMLDivElement>();
  const mapRef = useRef<HTMLDivElement>(null);
  const [activeMember, setActiveMember] = useState<number | null>(null);

  return (
    <SiteLayout navbarVariant="overlay">
      {/* Hero */}
      <section className="relative h-[600px] md:h-[760px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/b83d215482d53eb69b064b5c4f8c0569/e69b0ec7096b7b390bc35b38a29975d2.jpeg"
          alt="Vantory Labs office"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/20 to-ink/10" />

        <div ref={heroRef} className="relative h-full flex flex-col justify-end mx-auto max-w-[1320px] px-6 md:px-10 pb-16">
          <div data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-cream/60 mb-4 block">About us</span>
            <h1 className="text-display text-5xl md:text-7xl lg:text-[88px] text-cream leading-[0.95]">
              We build digital
              <br />
              <span className="italic font-light">products that matter.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Quick facts strip */}
      <section className="bg-lime-accent">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-ink/15">
            {[
              { label: "Founded", value: "2018" },
              { label: "Team members", value: "50+" },
              { label: "Projects delivered", value: "150+" },
              { label: "Countries", value: "12" },
            ].map((s) => (
              <div key={s.label} className="px-6 py-5">
                <div className="text-[10px] uppercase tracking-widest2 text-ink/60 mb-1">{s.label}</div>
                <div className="text-display text-xl md:text-2xl font-semibold text-ink">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our story */}
      <section ref={storyRef} className="bg-cream py-20 md:py-28">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4" data-reveal>
              <span className="text-xs uppercase tracking-widest2 text-ink/50">Our story</span>
              <h2 className="mt-4 text-display text-4xl md:text-5xl leading-[1.05]">
                From a small team
                <br />
                <span className="italic font-light">to a global agency.</span>
              </h2>
              <p className="mt-8 text-ink/70 text-base leading-relaxed">
                We are an IT company specializing in custom software development for businesses
                and private users, as well as comprehensive turnkey digital solutions.
              </p>

            </div>
            <div className="lg:col-span-8 lg:pl-8" data-reveal data-reveal-delay="80">
              <div className="space-y-0">
                {timeline.map((t) => (
                  <div
                    key={t.year}
                    className="group grid grid-cols-12 gap-6 py-7 border-b border-ink/8 last:border-0 hover:bg-sand/50 -mx-4 px-4 rounded-xl transition-colors duration-200 cursor-default"
                  >
                    <div className="col-span-2 md:col-span-1">
                      <span className="text-display text-sm text-ink/40 group-hover:text-terracotta transition-colors">{t.year}</span>
                    </div>
                    <div className="col-span-10 md:col-span-11">
                      <h3 className="text-display text-xl mb-2">{t.title}</h3>
                      <p className="text-ink/60 text-sm leading-relaxed">{t.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section ref={teamRef} className="bg-sand py-20 md:py-28 border-t border-ink/8">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="mb-10" data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-ink/50">Our team</span>
            <h2 className="mt-3 text-display text-3xl md:text-5xl">The people behind the code.</h2>
            <p className="mt-4 text-ink/70 text-base leading-relaxed max-w-2xl">
              Experienced project managers, developers, UI/UX designers, marketers, and QA engineers.
              United by passion for technology and commitment to excellence.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-reveal data-reveal-delay="80">
            {teamMembers.map((m, i) => (
              <div
                key={m.name}
                className="group relative rounded-3xl bg-cream border border-ink/5 p-8 hover:border-ink/15 transition-all duration-300 cursor-default"
                onMouseEnter={() => setActiveMember(i)}
                onMouseLeave={() => setActiveMember(null)}
              >
                <div
                  className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-6 transition-colors duration-300 ${
                    activeMember === i ? "bg-lime-accent" : "bg-ink/5 group-hover:bg-ink"
                  }`}
                >
                  <i className={`${m.icon} text-2xl ${activeMember === i ? "text-ink" : "text-ink/40 group-hover:text-lime-accent"} transition-colors`} />
                </div>
                <h3 className="text-display text-2xl mb-1">{m.name}</h3>
                <p className="text-xs uppercase tracking-widest2 text-ink/50 mb-4">{m.role}</p>
                <p className="text-ink/60 text-sm leading-relaxed mb-5">{m.bio}</p>
                <div className="flex flex-wrap gap-2">
                  {m.specialties.map((s) => (
                    <span key={s} className="rounded-full bg-ink/5 text-ink/70 px-3 py-1 text-[10px] uppercase tracking-widest2">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section ref={valuesRef} className="bg-cream py-20 md:py-28 border-t border-ink/8">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-14">
            <div className="lg:col-span-5" data-reveal>
              <span className="text-xs uppercase tracking-widest2 text-ink/50">How we work</span>
              <h2 className="mt-4 text-display text-3xl md:text-5xl leading-[1.05]">
                Principles we
                <br />
                <span className="italic font-light">never compromise on.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div
                key={v.title}
                data-reveal
                data-reveal-delay={`${i * 80}`}
                className="group rounded-3xl border border-ink/8 p-8 hover:border-ink/20 hover:bg-lime-accent/10 transition-all duration-300"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-ink mb-6">
                  <i className={`${v.icon} text-xl text-lime-accent`} />
                </div>
                <h3 className="text-display text-2xl mb-3">{v.title}</h3>
                <p className="text-ink/60 text-sm leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map + Visit */}
      <section className="bg-ink py-20 md:py-28 text-cream">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-xs uppercase tracking-widest2 text-cream/50">Find us</span>
              <h2 className="mt-4 text-display text-4xl md:text-5xl leading-[1.05]">
                Our office in <span className="italic font-light text-lime-accent">London.</span>
              </h2>
              <div className="mt-8 space-y-4 text-cream/70">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-lg text-lime-accent mt-0.5" />
                  <div>
                    <div className="text-cream font-medium">109-111 Fulham Palace Road</div>
                    <div className="text-sm">London, W6 8JA, United Kingdom</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-phone-line text-lg text-lime-accent mt-0.5" />
                  <div>
                    <div className="text-cream font-medium">Phone</div>
                    <div className="text-sm">+447451257361</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-lg text-lime-accent mt-0.5" />
                  <div>
                    <div className="text-cream font-medium">Email</div>
                    <div className="text-sm">support@vantorylabs.com</div>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="mt-9 inline-flex items-center gap-2 rounded-full bg-lime-accent text-ink px-7 py-4 text-xs font-semibold uppercase tracking-widest2 whitespace-nowrap cursor-pointer hover:bg-cream transition-colors"
              >
                Get in touch
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>
            <div ref={mapRef} className="rounded-3xl overflow-hidden h-[400px] border border-cream/10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2485.9!2d-0.222!3d51.485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f8c1b1b1b1b%3A0x0!2s109-111+Fulham+Palace+Road%2C+London+W6+8JA!5e0!3m2!1sen!2suk!4v1715000000000"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(30%) contrast(1.1)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Vantory Labs office location"
              />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}